import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { CaretDownSolid, CaretUpSolid } from "@icons";
import { useReorderItem } from "@src/components/reorder";
import { NO_INTERPOLATION_ESCAPE } from "@src/raya-lucaria/i18n/interpolation";
import { Header, flexRender } from "@tanstack/react-table";
import classNames from "classnames";
import { useId } from "react";

import { useTableContext } from "../context";
import { getColumnLabel } from "../utils";

export default function TableColumnHeader({
  header,
  isSortable,
}: {
  header: Header<unknown, unknown>;
  isSortable: boolean;
}) {
  const { editMode, t, tableId } = useTableContext();
  const sortStateId = useId();

  const toggleSortingHandler = header.column.getToggleSortingHandler();
  const sortDirection = header.column.getIsSorted();

  const isAscending = sortDirection === "asc";
  const isDescending = sortDirection === "desc";
  const sortStateText = (() => {
    if (isAscending) return t("sortStateAscending");
    if (isDescending) return t("sortStateDescending");
    return t("sortStateNone");
  })();

  const reorder = useReorderItem(header.id);
  const columnName = getColumnLabel(header.column);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    isDragging,
    transform,
  } = useDraggable({
    id: header.id,
    disabled: reorder.disabled,
    attributes: { roleDescription: reorder.roleDescription },
  });
  const showsSortButton = isSortable && !editMode;

  return (
    <div
      className="flex items-center gap-2"
      style={{
        transform: CSS.Translate.toString(transform),
      }}
    >
      <button
        type="button"
        className={classNames(
          "relative z-10 -my-2 min-h-target min-w-target-min flex gap-2 justify-center items-center cursor-grab focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus",
          {
            "opacity-80 cursor-grabbing": isDragging,
          },
        )}
        ref={(node) => {
          setNodeRef(node);
          setActivatorNodeRef(node);
        }}
        data-hit-layer="above"
        data-target-gap="column-drag"
        data-grid-primary={showsSortButton ? undefined : ""}
        {...reorder.handleProps(
          attributes,
          listeners,
          reorder.names.column(columnName),
        )}
      >
        <span
          id={`${tableId}-${header.id}-label`}
          className="block truncate bg-tint"
        >
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </span>
      </button>
      {showsSortButton && (
        <button
          className="flex flex-col items-center gap-1 text-muted cursor-pointer after:absolute after:inset-y-0 after:left-0 after:right-target-min after:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          onClick={toggleSortingHandler}
          type="button"
          data-grid-primary=""
          aria-label={t("sortByColumn", {
            column: columnName,
            interpolation: NO_INTERPOLATION_ESCAPE,
          })}
          aria-describedby={sortStateId}
        >
          <span
            className={classNames({
              "opacity-35": !isAscending,
            })}
          >
            <CaretUpSolid width={10} />
          </span>
          <span
            className={classNames({
              "opacity-35": !isDescending,
            })}
          >
            <CaretDownSolid width={10} />
          </span>
          <span id={sortStateId} className="sr-only">
            {sortStateText}
          </span>
        </button>
      )}
    </div>
  );
}
